import React, { useState } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { AUTH } from "../constants/testIds";
import { MapPin, ShieldCheck, KeyRound, ArrowLeft } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Password rules mirrored from backend auth_deps.validate_password_strength.
// Kept in-sync manually; backend is still the authority.
const PW_RULES = [
  { key: "len", label: "En az 12 karakter", test: (p) => p.length >= 12 },
  { key: "upper", label: "En az bir büyük harf", test: (p) => /[A-ZÇĞİÖŞÜ]/.test(p) },
  { key: "lower", label: "En az bir küçük harf", test: (p) => /[a-zçğıöşü]/.test(p) },
  { key: "digit", label: "En az bir rakam", test: (p) => /[0-9]/.test(p) },
  { key: "special", label: "En az bir özel karakter", test: (p) => /[^A-Za-zÇĞİÖŞÜçğıöşü0-9]/.test(p) },
];

const AuthPage = () => {
  const [mode, setMode] = useState("login"); // 'login' | 'reset'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [totpStep, setTotpStep] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  // Reset-mode local state
  const [resetEmail, setResetEmail] = useState("");
  const [resetCode, setResetCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const opts = {};
    if (totpStep) {
      if (useRecovery) opts.recovery_code = recoveryCode;
      else opts.totp_code = totpCode;
    }
    const result = await login(email, password, opts);
    if (result.success) {
      // done
    } else if (result.totp_required) {
      setTotpStep(true);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const goReset = () => {
    setMode("reset");
    setResetEmail(email || "");
    setError("");
    setResetSuccess(false);
  };

  const goLogin = () => {
    setMode("login");
    setError("");
    setResetSuccess(false);
    setResetCode("");
    setNewPassword("");
    setConfirmPassword("");
  };

  const handleReset = async (e) => {
    e.preventDefault();
    setError("");
    if (newPassword !== confirmPassword) {
      setError("Yeni şifreler eşleşmiyor.");
      return;
    }
    const failing = PW_RULES.filter((r) => !r.test(newPassword));
    if (failing.length > 0) {
      setError("Şifre kuralları karşılanmıyor.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(
        `${API_URL}/api/auth/reset-password`,
        {
          email: resetEmail.trim(),
          recovery_code: resetCode.trim(),
          new_password: newPassword,
        },
        { withCredentials: false }
      );
      setResetSuccess(true);
      setResetCode("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(
        typeof d === "string"
          ? d
          : Array.isArray(d)
          ? d.map((e) => e?.msg || JSON.stringify(e)).join(" ")
          : err.message || "Şifre sıfırlanamadı."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F9FAFB] to-[#E4E4E7] dark:from-[#09090B] dark:to-[#18181B]"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="w-full max-w-md p-8 bg-white dark:bg-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg shadow-sm">
        <div className="flex items-center justify-center mb-8">
          <MapPin className="w-10 h-10 text-[#F59E0B] mr-3" />
          <h1 className="text-3xl font-bold tracking-tight text-[#0A0A0A] dark:text-[#FAFAFA]">
            Emlak Takip
          </h1>
        </div>

        <h2 className="text-xl font-semibold mb-6 text-center text-[#0A0A0A] dark:text-[#FAFAFA]">
          {mode === "reset"
            ? "Şifre Sıfırlama"
            : totpStep
            ? "İki Aşamalı Doğrulama"
            : "Yönetici Girişi"}
        </h2>

        {mode === "login" && (
          <form onSubmit={handleSubmit} data-testid={AUTH.loginForm} className="space-y-4">
            {!totpStep && (
              <>
                <div>
                  <Label htmlFor="email" className="text-[#52525B] dark:text-[#A1A1AA]">E-posta</Label>
                  <Input
                    id="email"
                    data-testid={AUTH.loginEmail}
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[#52525B] dark:text-[#A1A1AA]">Şifre</Label>
                  <Input
                    id="password"
                    data-testid={AUTH.loginPassword}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                  />
                </div>
              </>
            )}

            {totpStep && !useRecovery && (
              <div>
                <div className="flex items-center gap-2 mb-2 text-[#52525B] dark:text-[#A1A1AA]">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="text-sm">Authenticator uygulamanızdaki 6 haneli kodu girin</span>
                </div>
                <Input
                  data-testid="login-totp-code"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="123456"
                  autoFocus
                  className="text-center text-2xl tracking-[0.5em] bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                />
                <button
                  type="button"
                  onClick={() => setUseRecovery(true)}
                  className="mt-3 text-xs text-[#F59E0B] hover:underline"
                  data-testid="login-use-recovery-code"
                >
                  Telefonuma erişemiyorum, kurtarma kodu kullan
                </button>
              </div>
            )}

            {totpStep && useRecovery && (
              <div>
                <Label className="text-[#52525B] dark:text-[#A1A1AA]">Kurtarma Kodu</Label>
                <Input
                  data-testid="login-recovery-code"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  placeholder="XXXXX-XXXXX"
                  autoFocus
                  className="mt-1 text-center tracking-widest bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
                />
                <button
                  type="button"
                  onClick={() => setUseRecovery(false)}
                  className="mt-3 text-xs text-[#F59E0B] hover:underline"
                >
                  TOTP koduna geri dön
                </button>
              </div>
            )}

            {error && (
              <div
                data-testid="login-error"
                className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid={AUTH.loginSubmit}
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:hover:bg-[#E4E4E7] transition-colors duration-200"
            >
              {loading ? "Yükleniyor..." : totpStep ? "Doğrula" : "Giriş Yap"}
            </Button>

            {totpStep && (
              <button
                type="button"
                onClick={() => { setTotpStep(false); setTotpCode(""); setRecoveryCode(""); setUseRecovery(false); }}
                className="w-full text-xs text-[#52525B] dark:text-[#A1A1AA] hover:underline"
              >
                ← Geri (şifre ekranı)
              </button>
            )}

            {!totpStep && (
              <button
                type="button"
                onClick={goReset}
                data-testid="login-forgot-password-link"
                className="w-full text-xs text-[#F59E0B] hover:underline inline-flex items-center justify-center gap-1"
              >
                <KeyRound className="w-3 h-3" /> Şifremi unuttum
              </button>
            )}
          </form>
        )}

        {mode === "reset" && !resetSuccess && (
          <form onSubmit={handleReset} data-testid="reset-form" className="space-y-4">
            <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] bg-[#F4F4F5] dark:bg-[#27272A] p-3 rounded border border-[#E4E4E7] dark:border-[#3F3F46]">
              Sıfırlama kodunuzu <span className="font-mono">create_password_reset_code.py</span> yardımcı komutuyla sunucuda üretebilirsiniz. Kod tek kullanımlıktır ve 30 dakika geçerlidir.
            </p>

            <div>
              <Label className="text-[#52525B] dark:text-[#A1A1AA]">E-posta</Label>
              <Input
                data-testid="reset-email"
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                required
                autoComplete="username"
                className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>

            <div>
              <Label className="text-[#52525B] dark:text-[#A1A1AA]">Sıfırlama Kodu</Label>
              <Input
                data-testid="reset-code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                placeholder="XXXX-XXXX-XXXX"
                required
                autoComplete="one-time-code"
                className="mt-1 text-center tracking-widest bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>

            <div>
              <Label className="text-[#52525B] dark:text-[#A1A1AA]">Yeni Şifre</Label>
              <Input
                data-testid="reset-new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
              />
              <ul className="mt-2 text-xs space-y-1" aria-label="Şifre kuralları">
                {PW_RULES.map((r) => {
                  const ok = newPassword.length > 0 && r.test(newPassword);
                  return (
                    <li
                      key={r.key}
                      data-testid={`reset-rule-${r.key}`}
                      className={ok ? "text-green-600" : "text-[#71717A]"}
                    >
                      {ok ? "✓" : "•"} {r.label}
                    </li>
                  );
                })}
              </ul>
            </div>

            <div>
              <Label className="text-[#52525B] dark:text-[#A1A1AA]">Yeni Şifre (Tekrar)</Label>
              <Input
                data-testid="reset-confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="mt-1 bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
              />
            </div>

            {error && (
              <div
                data-testid="reset-error"
                className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded"
              >
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="reset-submit"
              disabled={loading}
              className="w-full bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:hover:bg-[#E4E4E7]"
            >
              {loading ? "Sıfırlanıyor..." : "Şifreyi Sıfırla"}
            </Button>

            <button
              type="button"
              onClick={goLogin}
              data-testid="reset-back-to-login"
              className="w-full text-xs text-[#52525B] dark:text-[#A1A1AA] hover:underline inline-flex items-center justify-center gap-1"
            >
              <ArrowLeft className="w-3 h-3" /> Giriş ekranına dön
            </button>
          </form>
        )}

        {mode === "reset" && resetSuccess && (
          <div className="space-y-4" data-testid="reset-success">
            <div className="text-sm text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 p-4 rounded border border-green-200 dark:border-green-800">
              Şifreniz güncellendi ve tüm eski oturumlar iptal edildi. Yeni şifrenizle giriş yapabilirsiniz.
            </div>
            <Button
              onClick={goLogin}
              data-testid="reset-goto-login"
              className="w-full bg-[#0A0A0A] hover:bg-[#18181B] text-white dark:bg-[#FAFAFA] dark:text-[#0A0A0A] dark:hover:bg-[#E4E4E7]"
            >
              Giriş Yap
            </Button>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-xs text-[#52525B] dark:text-[#A1A1AA]">
            Sadece yönetici hesabı ile giriş yapılabilir
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
